import React, { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea } from 'recharts';
import { useFinancial, normalizeToMonthly } from '../../context/FinancialContext';

const COLORS = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 backdrop-blur-sm p-3 md:p-4 rounded-xl shadow-xl border border-slate-100 text-xs md:text-sm z-50 max-w-[200px] md:max-w-xs">
        <p className="font-black text-slate-900 mb-2">Age {label}</p>
        <div className="space-y-1">
          {payload.map((entry, index) => {
            const isTotal = entry.name === 'Accumulated Wealth';
            const isReal = entry.name === 'Real Purchasing Power';
            
            // Compact currency for mobile to prevent overflow
            const formattedVal = new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              notation: window.innerWidth < 768 ? 'compact' : 'standard',
              maximumFractionDigits: 1
            }).format(entry.value);

            return (
              <div key={index} className="flex items-center justify-between gap-3">
                <span style={{ color: entry.color }} className="font-bold truncate max-w-[100px]">
                  {entry.name}
                </span>
                <span className="font-black text-slate-700">{formattedVal}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  return null;
};

const WealthProjectionChart = ({ selectedItems = ['total'] }) => {
  const { state } = useFinancial();
  const { goals, calculations, investmentSchedule, investmentBuckets, roadmapProgress } = state;

  const chartData = useMemo(() => {
    const data = [];
    const yearsToGoal = Math.max(1, goals.targetAge - goals.currentAge);
    const monthlyROI = (goals.expectedROI / 100) / 12;
    let portfolioTotal = parseFloat(goals.startingNetWorth) || 0;

    const bucketValues = investmentBuckets.reduce((acc, b) => {
      acc[b.id] = 0;
      return acc;
    }, {});

    // Year 0
    const year0 = { age: goals.currentAge, yearIndex: 0 };
    if (selectedItems.includes('total')) {
      year0.total = Math.round(portfolioTotal);
      year0.realTotal = Math.round(portfolioTotal);
    }
    investmentBuckets.forEach(b => {
      if (selectedItems.includes(b.id.toString())) year0[`bucket_${b.id}`] = 0;
    });
    data.push(year0);

    for (let year = 1; year <= yearsToGoal; year++) {
      const isDownturnYear = goals.downturnYear > 0 && year >= goals.downturnYear && year < (goals.downturnYear + goals.downturnDuration);
      
      let curROI = monthlyROI;
      if (isDownturnYear) {
        curROI = Math.pow(1 - (goals.downturnSeverity / 100), 1/12) - 1;
      }

      const baseInvestable = calculations.monthlyInvested;
      let monthlyContributionTotal = baseInvestable;
      
      if (investmentSchedule.type === 'linear_step') {
        const normalizedStep = normalizeToMonthly(investmentSchedule.stepAmount, investmentSchedule.stepFrequency || 'monthly');
        monthlyContributionTotal = baseInvestable + ((year - 1) * normalizedStep);
      }

      for (let month = 0; month < 12; month++) {
        portfolioTotal = (portfolioTotal + monthlyContributionTotal) * (1 + curROI);

        investmentBuckets.forEach(bucket => {
          const ratio = bucket.amount / (baseInvestable || 1);
          let bucketContrib = (monthlyContributionTotal * ratio);
          bucketValues[bucket.id] = (bucketValues[bucket.id] + bucketContrib) * (1 + curROI);
        });
      }

      const dataPoint = { age: goals.currentAge + year, yearIndex: year };
      
      if (selectedItems.includes('total')) {
        dataPoint.total = Math.round(portfolioTotal);
        // Inflation math: Real Value = Nominal / (1+i)^t
        dataPoint.realTotal = Math.round(portfolioTotal / Math.pow(1 + (goals.inflationRate / 100), year));
      }

      investmentBuckets.forEach(b => {
        if (selectedItems.includes(b.id.toString())) {
          dataPoint[`bucket_${b.id}`] = Math.round(bucketValues[b.id]);
        }
      });

      data.push(dataPoint);
    }

    return data;
  }, [goals, calculations, investmentSchedule, investmentBuckets, selectedItems]);

  const formatCurrencyAxis = (val) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(0)}K`;
    return `$${val}`;
  };

  return (
    <div className="w-full h-full touch-pan-y" style={{ minHeight: '300px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="colorReal" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.1}/>
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis 
            dataKey="age" 
            stroke="#94a3b8" 
            fontSize={10} 
            fontWeight={700}
            tickLine={false} 
            axisLine={false} 
            dy={10} 
          />
          <YAxis 
            stroke="#94a3b8" 
            fontSize={10} 
            fontWeight={700}
            tickFormatter={formatCurrencyAxis}
            tickLine={false} 
            axisLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          
          {/* Shaded Crash Zones */}
          {goals.downturnYear > 0 && (
            <ReferenceArea 
              x1={goals.currentAge + goals.downturnYear} 
              x2={goals.currentAge + goals.downturnYear + goals.downturnDuration - 1} 
              fill="#fee2e2" 
              fillOpacity={0.5} 
              stroke="#fca5a5"
              strokeDasharray="3 3"
            />
          )}

          {selectedItems.includes('total') && (
            <>
              <Area 
                type="monotone" 
                dataKey="total" 
                name="Accumulated Wealth"
                stroke="#3b82f6" 
                strokeWidth={4}
                fill="url(#colorTotal)" 
                animationDuration={1200}
              />
              <Area 
                type="monotone" 
                dataKey="realTotal" 
                name="Real Purchasing Power"
                stroke="#ef4444" 
                strokeWidth={2}
                strokeDasharray="5 5"
                fill="url(#colorReal)" 
                animationDuration={1500}
              />
            </>
          )}

          {investmentBuckets.map((bucket, idx) => {
            if (!selectedItems.includes(bucket.id.toString())) return null;
            const color = COLORS[(idx + 1) % COLORS.length];
            return (
              <Area 
                key={bucket.id}
                type="monotone" 
                dataKey={`bucket_${bucket.id}`} 
                name={bucket.name}
                stroke={color} 
                strokeWidth={3}
                fill="none" 
                animationDuration={1000}
              />
            );
          })}

          {/* Dotted target line only shows if FI number step is completed in the roadmap */}
          {roadmapProgress.includes('fi_number') && (
            <ReferenceLine 
              y={state.goals.targetAmount} 
              stroke="#94a3b8" 
              strokeDasharray="8 8"
              label={{ position: 'insideTopRight', value: 'TARGET', fill: '#94a3b8', fontSize: 10, fontWeight: 900 }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default WealthProjectionChart;